/**
 * ALOHA PLATFORM
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Crown, LogOut, User } from 'lucide-react';

const Navbar = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getTierBadge = (tier) => {
    const badges = {
      'FREE': { bg: 'bg-gray-500', text: 'Free' },
      'STARTER': { bg: 'bg-blue-500', text: 'Starter' },
      'PRO': { bg: 'bg-purple-500', text: 'Pro' },
      'ENTERPRISE': { bg: 'bg-gradient-to-r from-yellow-500 to-orange-500', text: 'Enterprise' },
      'LIFETIME_ENTERPRISE': { bg: 'bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600', text: 'Lifetime' }
    };
    
    const badge = badges[tier] || badges['FREE'];
    
    return (
      <span className={`${badge.bg} text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}>
        {tier === 'LIFETIME_ENTERPRISE' && <Crown className="w-3 h-3" />}
        {badge.text}
      </span>
    );
  };

  return (
    <nav className="bg-gradient-to-r from-purple-900 via-blue-900 to-indigo-900 text-white shadow-lg">
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <div className="text-2xl font-bold bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-400 bg-clip-text text-transparent">
              👑 Queen Supernova
            </div>
          </Link>

          <div className="flex items-center space-x-6">
            {isAuthenticated ? (
              <>
                <Link to="/hub" className="hover:text-yellow-300 transition font-bold">
                  🌐 Hub
                </Link>
                <Link to="/dashboard" className="hover:text-yellow-300 transition">
                  Dashboard
                </Link>
                <Link to="/game" className="hover:text-yellow-300 transition">
                  🎮 Game
                </Link>
                <Link to="/streaming" className="hover:text-yellow-300 transition">
                  📺 Streaming
                </Link>
                <Link to="/avatar-chat" className="hover:text-yellow-300 transition">
                  AI Agent
                </Link>
                <Link to="/pricing" className="hover:text-yellow-300 transition">
                  Pricing
                </Link>
                <Link to="/profile" className="hover:text-yellow-300 transition">
                  Profile
                </Link>
                {(user.subscription_tier === 'LIFETIME_ENTERPRISE' || user.is_admin) && (
                  <Link to="/admin" className="hover:text-yellow-300 transition">
                    Admin
                  </Link>
                )}
                
                <div className="flex items-center space-x-3">
                  {getTierBadge(user.subscription_tier)}
                  
                  <div className="flex items-center space-x-2">
                    <User className="w-5 h-5" />
                    <span className="text-sm">{user.email}</span>
                  </div>
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center space-x-1 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Logout</span>
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link to="/pricing" className="hover:text-yellow-300 transition">
                  Pricing
                </Link>
                <Link
                  to="/login"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition"
                  data-testid="login-link"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 rounded-lg transition font-semibold"
                  data-testid="signup-link"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
